import { ReactNode, useState } from 'react'
import { QueryClientProvider } from '@tanstack/react-query'

import { InjectionTokens } from '@/controller/tokens'

import { ILogger } from '@/infra/logger/ILogger'
import { useInjection } from '@/presentation/contexts/InjectionContext'
import { createQueryClient } from '@/presentation/queries/queryClient'
import { useDomainEventInvalidation } from '@/presentation/queries/useDomainEventInvalidation'

interface QueryProviderProps {
  children: ReactNode
}

/** Liga a ponte de eventos de domínio por dentro do provider, para haver um único ponto de fiação. */
function DomainEventBridge({ children }: QueryProviderProps) {
  useDomainEventInvalidation()

  return <>{children}</>
}

/**
 * Provider do React Query.
 *
 * Fica DENTRO do `InjectionProvider` porque precisa do `Logger` para o `onError` global do cache —
 * e o `App` já prova que `useInjection` funciona nessa posição. Fica FORA do `AppErrorBoundary`
 * porque um reset da boundary não deve descartar o cache.
 */
export function QueryProvider({ children }: QueryProviderProps) {
  const logger = useInjection<ILogger>(InjectionTokens.Logger)
  // `useState` com initializer: cria uma instância só, e não uma por render.
  const [queryClient] = useState(() => createQueryClient({ logger }))

  return (
    <QueryClientProvider client={queryClient}>
      <DomainEventBridge>{children}</DomainEventBridge>
    </QueryClientProvider>
  )
}
